import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { User } from 'src/app/common/user/user';
import { UserService } from 'src/app/common/user/user.service';
import {
  forgotPasswordRoutePath,
  getLoginChildRoutePath,
  getLoginRoutePath,
  getUserRoutePath,
  welcomeRoutePath,
} from '../../app.routes';
import { AlertService } from '../../common/alert/alert.service';
import { AuthService } from '../../common/auth/auth.service';
import { InviteService } from '../../common/invite/invite.service';

export async function canActivateLayout(
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): Promise<boolean | UrlTree> {
  const alertService = inject(AlertService);
  const authService = inject(AuthService);
  const inviteService = inject(InviteService);
  const userService = inject(UserService);
  const router = inject(Router);

  const fragment = route.fragment;
  const path = state.url?.split('?')[0] ?? '';
  const searchParams = new URLSearchParams(route.queryParams);
  const inviteCode = searchParams.get('invite_code');

  // Store the invite code for later redemption
  if (inviteCode) {
    inviteService.storeInviteCode(inviteCode, location.href);
  }

  // Create initial redirect (may include invite code if not authenticated yet)
  const redirectWithInvite = createRedirect(path, searchParams);

  if (fragment) {
    const params = new URLSearchParams(fragment);
    const errorCode = params.get('error_code');
    let message = params.get('error_description') || 'Unknown error';

    if (errorCode === 'otp_expired') {
      message =
        'Your password reset link has expired. Please request a new one.';
    }

    if (params.get('error')) {
      await alertService.createErrorNotice(message);
    }

    if (errorCode === 'otp_expired') {
      return router.createUrlTree([
        getLoginChildRoutePath(forgotPasswordRoutePath),
      ]);
    }

    if (errorCode) {
      return router.navigateByUrl(getLoginRoutePath(redirectWithInvite));
    }
  }

  const authUser = await authService.getAuthUser();

  if (!authUser) {
    // Keep invite code in URL for login flow
    return router.navigateByUrl(getLoginRoutePath(redirectWithInvite));
  }

  if (inviteCode) {
    // User is authenticated, safe to remove invite code from URL now
    searchParams.delete('invite_code');
  }

  // Update redirect to exclude invite code
  const redirect = createRedirect(path, searchParams);

  let user: User | null = null;

  try {
    const users = await userService.getUsers();
    user = users.find((user) => user.email === authUser.email) ?? null;
  } catch (error: unknown) {
    const message = (error as Error).message || 'Unknown error';
    await alertService.createNoticeWithOptions({
      message,
      duration: 5000,
      position: 'bottom',
      color: 'danger',
    });
  }

  if (!user) {
    return router.navigateByUrl(getLoginRoutePath(redirect));
  }

  if (inviteService.hasPendingCode()) {
    try {
      await inviteService.redeemStoredCode();
      await alertService.createNotice(
        'Invitation accepted! You now have viewer access.',
      );
    } catch (unknown: unknown) {
      const error = unknown as Error;
      await alertService.createErrorNotice(
        error?.message || 'Failed to redeem invitation',
      );
      await inviteService.clearStoredCode();
    }
  }

  if (!user.full_name?.trim() || !user.date_of_birth || !user.sex) {
    await alertService.createNoticeWithOptions({
      message: 'Please complete your profile to continue',
      duration: 5000,
      position: 'bottom',
    });

    // Allow the user to set their profile.
    // If they were invited to view a specific user's profile,
    // they will be redirected to that user's profile after they complete their profile.
    return router.createUrlTree([getUserRoutePath(user.id, welcomeRoutePath)], {
      queryParams: { redirect },
    });
  }

  if (inviteCode) {
    return router.navigateByUrl(redirect);
  }

  return true;
}

function createRedirect(path: string, searchParams: URLSearchParams) {
  const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
  return path == '/' ? '' : `${path}${query}`;
}
